import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import type { ApiError } from '../types/common';

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  status: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUserDetail extends AdminUser {
  dateOfBirth?: string;
  sessionCount: number;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminUsersFilters {
  search?: string;
  page?: number;
  limit?: number;
}

const adminUserKeys = {
  all: ['admin-users'] as const,
  list: (filters?: AdminUsersFilters) => [...adminUserKeys.all, 'list', filters] as const,
  detail: (id: string) => [...adminUserKeys.all, 'detail', id] as const,
};

async function fetchAdminUsers(filters?: AdminUsersFilters): Promise<AdminUsersResponse> {
  const params: Record<string, string> = {};
  if (filters?.search) params.search = filters.search;
  if (filters?.page) params.page = String(filters.page);
  if (filters?.limit) params.limit = String(filters.limit);

  const response = await apiClient.get<AdminUsersResponse>('/admin/users', { params });
  return response.data;
}

async function fetchAdminUserDetail(id: string): Promise<AdminUserDetail> {
  const response = await apiClient.get<AdminUserDetail>(`/admin/users/${id}`);
  return response.data;
}

async function updateUserStatus(id: string, status: string): Promise<{ message: string; userId: string; status: string }> {
  const response = await apiClient.put(`/admin/users/${id}/status`, { status });
  return response.data;
}

export function useAdminUsers(filters?: AdminUsersFilters) {
  return useQuery<AdminUsersResponse, ApiError>({
    queryKey: adminUserKeys.list(filters),
    queryFn: () => fetchAdminUsers(filters),
    staleTime: 30 * 1000,
  });
}

export function useAdminUserDetail(id: string) {
  return useQuery<AdminUserDetail, ApiError>({
    queryKey: adminUserKeys.detail(id),
    queryFn: () => fetchAdminUserDetail(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useUpdateUserStatus() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string; userId: string; status: string }, ApiError, { id: string; status: string }>({
    mutationFn: ({ id, status }) => updateUserStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(id) });
    },
  });
}
